import './picker.css'

import { DropDownButton } from '@progress/kendo-react-buttons';
import {
    TimePicker as KendoTimePicker,
} from '@progress/kendo-react-dateinputs';
import { useState } from 'react';
import { isNullOrEmpty } from '../../helpers/ObjectHelpers';
import { DropDownButtonActionRender } from '../render/DropDownButtonActionRender';
import Icons from '../icons/Icons';
import moment from 'moment';
import { useAdaptiveVisibility } from '../../hooks/useAdaptiveVisibility';
import { TimePickerProps } from './props/TimePickerProps';
import { usePickerItems } from './hooks/usePickerItems';
import { usePickerEventListeners } from './hooks/usePickerEventListeners';
import { usePickerElementPattern } from './hooks/usePickerElementPattern';
import { usePickerValueChange } from './hooks/usePickerValueChange';
import { usePickerSharedValues } from './hooks/usePickerSharedValues';
import { useAdaptiveValue } from '../../hooks/useAdaptiveValue';

export const TimePicker = ({
    title = "",
    value = null,
    placeholder = "Velg tidspunkt",
    disabled = false,
    hasValidationError = false,
    canDelete = false,
    adaptive = true,
    showPrefix = false,
    className = "",
    onChange,
}: TimePickerProps) => {
    
    const [prefix, setPrefix] = useState("");

    const {
        items, setItems,
        show, setShow,
        localValue, setLocalValue,
        localDisabled, setLocalDisabled
    } = usePickerSharedValues(disabled);

    // *************************
    // todo: remove this fix when KendoDateTimePicker adaptive view is fixed for react 19
    useAdaptiveVisibility(show, adaptive, disabled, setLocalDisabled);
    // *************************

    useAdaptiveValue(show, adaptive, setLocalValue, localValue, value);
    usePickerItems(canDelete, setItems, value);
    usePickerEventListeners(setShow);
    usePickerElementPattern();
    usePickerValueChange(setLocalValue, localValue, value, setPrefix);

    return (
        <div className={`date-picker ${className} ${hasValidationError ? "date-validation-error" : ""}`}
            style={{ gridTemplateColumns: showPrefix ? "auto 1fr auto" : "1fr auto" } }>
            <div className="date-prefix font-size-lg" style={{ display: showPrefix ? "grid" : "none" }}>
                {prefix}
            </div>
            <div className={`date-component ${showPrefix ? "date-component-with-prefix" : ""}`}>
                <KendoTimePicker
                    adaptiveTitle={isNullOrEmpty(title) ? "Velg tidspunkt" : `Velg ${title.toLowerCase()}`}
                    value={localValue}
                    placeholder={placeholder}
                    disabled={localDisabled}
                    show={show}
                    adaptive={adaptive}
                    onClose={() => {
                        setShow(false);
                        if (!isNullOrEmpty(prefix)) return;
                        setLocalValue(value);
                    }}
                    onChange={handleOnChange}
                    className="date-size"
                />
            </div>
            <div className="date-button">
                <DropDownButton
                    className="date-time-picker-button"
                    items={items}
                    disabled={disabled}
                    itemRender={DropDownButtonActionRender}
                    onItemClick={onItemClick}
                    text={<Icons iconName="clock" dimensions={28} />}
                />
            </div>
        </div>
    )

    function onItemClick(e: any) {
        switch (e.item.event) {
            case "delete":
                if (!onChange) return;
                onTimeChange(null);
                break;
            case "now":
                onTimeChange(moment());
                break;
            default:
                setShow(true);
                break;
        }
    }

    function handleOnChange(e: any) {
        if (!e.value) return;

        const value = e.value;
        const valueMoment = moment(value);
        onTimeChange(valueMoment);
    }

    function onTimeChange(date: any | null) {
        if (date) {
            const newDate = moment();

            newDate.set("hour", 0);
            newDate.set("minute", 0);
            newDate.set("second", 0);
            newDate.set("millisecond", 0);

            const dateMinutes = getTotalMinutes(date);
            const currentMinutes = getTotalMinutes(moment());

            if (dateMinutes < currentMinutes) {
                newDate.add(1, "d"); // add extra day
            }

            newDate.set("hour", date.hours());
            newDate.set("minute", date.minutes());

            setPrefix(newDate.format("DD.MM"));
            setLocalValue(newDate.toDate());

            onResponse(newDate.toDate());
        } else {
            onResponse();
        }
    }

    function onResponse(e: any | null = null) {
        if (!onChange) return;
        onChange(e);
    }

    function getTotalMinutes(date: any) {
        return date.hours() * 60 + date.minutes();
    }
}
